import { zodResolver } from '@hookform/resolvers/zod'
import type { HttpError } from '@refinedev/core'
import { useCan, useOne, useSelect } from '@refinedev/core'
import { useForm } from '@refinedev/react-hook-form'
import type {
  Cabinet,
  CabinetLanguage,
  Location,
  Tenant,
} from '@sirius/contracts/admin'
import { cabinetCreateInputSchema } from '@sirius/contracts/admin'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { InheritableSelect } from '~/components/inheritableSelect'
import { Button } from '~/components/ui/button'
import { Checkbox } from '~/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import {
  FORM_RECORD_QUERY_OPTIONS,
  useRecordPending,
} from '~/lib/formRecordQuery'
import { USE_DEFAULT, effective, fromOwn, toOwn } from '~/lib/inheritable'
import { NotFoundPage } from '~/pages/notFound'
import {
  cabinetDefaults,
  useTenantDefaultConfigurations,
} from '~/pages/settings/defaultConfigurations'
import { CONFIGURE_CABINETS_ACTION } from '~/providers/accessControl'
import { useActiveTenant } from '~/tenancy/activeTenant'
import {
  LANGUAGE_NAMES,
  LANGUAGE_OPTIONS,
  isCabinetLanguage,
} from './cabinetLanguage'
import { STORAGE, fromLocationId, toLocationId } from './cabinetStatus'
import { ConfigurationSelect } from './configurationSelect'
import { fromConfigurationId, gameConfigsInput } from './configurations'
import { useGameOptions } from './useGameOptions'

/**
 * name + location + language choice (select value) + the games it offers + (staff
 * and the tenant's owners/admins) the configuration of each game (game id →
 * select value); tenant from the host, status via the status actions.
 */
const cabinetFormSchema = z.object({
  name: cabinetCreateInputSchema.shape.name,
  location: z.string().min(1),
  languageChoice: z.string(),
  games: z.array(z.string()),
  configurations: z.record(z.string(), z.string()),
})
type CabinetFormValues = z.input<typeof cabinetFormSchema>
type CabinetVariables = {
  name: string
  locationId: string | null
  language: CabinetLanguage | null
  gameIds: string[]
  gameConfigs?: { gameId: string; configurationId: string | null }[]
}

/**
 * `Low volatility` for the configuration assigned to the cabinet's game, if
 * any.
 */
function assignedLabel(record: Cabinet | undefined, gameId: string) {
  const entry = record?.gameConfigs.find((config) => config.gameId === gameId)
  if (entry === undefined || entry.configurationId === null) {
    return undefined
  }
  return entry.assignedConfigurationName ?? undefined
}

export function CabinetForm({
  action,
  id,
}: {
  action: 'create' | 'edit'
  id?: string
}) {
  const { t } = useTranslation()
  const form = useForm<Cabinet, HttpError, CabinetFormValues>({
    resolver: zodResolver(cabinetFormSchema),
    defaultValues: {
      name: '',
      location: STORAGE,
      languageChoice: USE_DEFAULT,
      games: [],
      configurations: {},
    },
    refineCoreProps: {
      resource: 'cabinets',
      action,
      id,
      redirect: 'show',
      queryOptions: FORM_RECORD_QUERY_OPTIONS,
      onMutationError: (error) => {
        // the API names the fields `locationId`, `language`, `gameIds` and
        // `gameConfigs`, the form `location`, `languageChoice`, `games` and
        // `configurations`
        const location = error.errors?.locationId
        if (typeof location === 'string') {
          form.setError('location', { message: location })
        }
        const language = error.errors?.language
        if (typeof language === 'string') {
          form.setError('languageChoice', { message: language })
        }
        const games = error.errors?.gameIds
        if (typeof games === 'string') {
          form.setError('games', { message: games })
        }
        const configurations = error.errors?.gameConfigs
        if (typeof configurations === 'string') {
          form.setError('configurations', { message: configurations })
        }
      },
    },
  })
  const { onFinish, formLoading, query } = form.refineCore
  // in edit mode nothing may be typed or saved before the record has settled
  const pending = useRecordPending(action, query)
  const record = query?.data?.data
  const { reset } = form
  // staff and the host tenant's owners/admins assign configurations
  // (FORBIDDEN for members, who keep the form without them)
  const { data: access } = useCan({
    resource: 'cabinets',
    action: CONFIGURE_CABINETS_ACTION,
  })
  const canConfigure = access?.can === true
  // what Use default means: on edit the cabinet's own `inheritedLanguage`, on
  // create the host tenant's default language (any tenant user may read it)
  const tenant = useActiveTenant()
  const { result: hostTenant } = useOne<Tenant, HttpError>({
    resource: 'tenants',
    id: tenant.id,
    queryOptions: { enabled: action === 'create' },
  })
  // what a game's Use default means: the cabinet's own
  // `inheritedConfigurationId`, else (on create, or a game just added) the
  // host tenant's default of it, else the game's
  const tenantDefaults = useTenantDefaultConfigurations(tenant.id, canConfigure)
  const defaultsByGame = cabinetDefaults(tenantDefaults.data)
  const inheritedConfiguration = (gameId: string) => {
    const entry = record?.gameConfigs.find((config) => config.gameId === gameId)
    return entry === undefined
      ? defaultsByGame.get(gameId)
      : entry.inheritedConfigurationId
  }
  const inheritedLanguage =
    action === 'edit'
      ? record?.inheritedLanguage
      : hostTenant &&
        effective(
          hostTenant.defaultLanguage,
          hostTenant.inheritedDefaultLanguage,
        )

  // `location`, `languageChoice`, `games` and `configurations` are not
  // fields of the record, so Refine can't fill them in; they must not be:
  // Refine would write the record's `language` (null for Use default) over
  // the select's `USE_DEFAULT`, and the select would show nothing
  useEffect(() => {
    if (record !== undefined) {
      reset({
        name: record.name,
        location: fromLocationId(record.locationId),
        languageChoice: fromOwn(record.language),
        games: record.gameIds,
        configurations: Object.fromEntries(
          record.gameConfigs.map(({ gameId, configurationId }) => [
            gameId,
            fromConfigurationId(configurationId),
          ]),
        ),
      })
    }
  }, [record, reset])

  const locations = useSelect<Location, HttpError>({
    resource: 'locations',
    optionLabel: 'name',
    optionValue: 'id',
    pagination: { mode: 'off' },
    sorters: [{ field: 'name', order: 'asc' }],
  })
  const games = useGameOptions()
  const offered = form.watch('games')

  if (query?.error?.statusCode === 404) {
    return <NotFoundPage />
  }
  if (query?.error) {
    return (
      <p
        role="alert"
        className="text-destructive text-sm"
      >
        {t('cabinetsPage.common.loadError', { message: query.error.message })}
      </p>
    )
  }

  const submit = ({
    name,
    location,
    languageChoice,
    games: gameIds,
    configurations,
  }: CabinetFormValues) =>
    onFinish({
      name,
      locationId: toLocationId(location),
      // every tenant user may set it, so the form always sends it
      language: toOwn(languageChoice, isCabinetLanguage),
      gameIds,
      ...(canConfigure && {
        gameConfigs: gameConfigsInput(gameIds, configurations),
      }),
    } satisfies CabinetVariables as never)

  return (
    <Form {...form}>
      <form
        aria-busy={pending}
        onSubmit={form.handleSubmit(submit)}
        className="max-w-md space-y-4"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('cabinetsPage.form.name')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={pending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('cabinetsPage.form.location')}</FormLabel>
              <Select
                value={field.value}
                disabled={pending}
                // Radix reports '' while the value has no item yet (locations
                // still loading); that would wipe the record's location
                onValueChange={(value) => {
                  if (value !== '') {
                    field.onChange(value)
                  }
                }}
              >
                <FormControl>
                  <SelectTrigger aria-label={t('cabinetsPage.form.location')}>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={STORAGE}>
                    {t('cabinetsPage.common.inStorage')}
                  </SelectItem>
                  {locations.options.map((option) => (
                    <SelectItem
                      key={String(option.value)}
                      value={String(option.value)}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {locations.query.isError && (
                <FormDescription className="text-destructive">
                  {t('cabinetsPage.form.locationsLoadError')}
                </FormDescription>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="languageChoice"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('cabinetsPage.form.language')}</FormLabel>
              <InheritableSelect
                aria-label={t('cabinetsPage.form.language')}
                value={field.value}
                disabled={pending}
                onChange={field.onChange}
                options={LANGUAGE_OPTIONS}
                inheritedLabel={
                  inheritedLanguage === undefined
                    ? undefined
                    : LANGUAGE_NAMES[inheritedLanguage]
                }
              />
              <FormDescription>
                {t('cabinetsPage.form.languageHint')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="games"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormControl>
                <fieldset className="grid gap-2">
                  <legend
                    data-error={fieldState.error !== undefined}
                    className="data-[error=true]:text-destructive mb-2 text-sm leading-none font-medium"
                  >
                    {t('cabinetsPage.form.games')}
                  </legend>
                  {games.options.map((option) => {
                    const checkboxId = `cabinet-game-${option.id}`
                    return (
                      <div
                        key={option.id}
                        className="flex items-center gap-2"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={field.value.includes(option.id)}
                          disabled={pending}
                          onCheckedChange={(checked) =>
                            field.onChange(
                              checked === true
                                ? [...field.value, option.id]
                                : field.value.filter(
                                    (gameId) => gameId !== option.id,
                                  ),
                            )
                          }
                        />
                        <Label
                          htmlFor={checkboxId}
                          className="font-normal"
                        >
                          {option.name}
                        </Label>
                      </div>
                    )
                  })}
                  {games.query.isLoading && (
                    <p className="text-muted-foreground text-sm">
                      {t('cabinetsPage.form.gamesLoading')}
                    </p>
                  )}
                  {games.query.isSuccess && games.options.length === 0 && (
                    <p className="text-muted-foreground text-sm">
                      {t('cabinetsPage.form.gamesEmpty')}
                    </p>
                  )}
                </fieldset>
              </FormControl>
              {games.query.isError && (
                <FormDescription className="text-destructive">
                  {t('cabinetsPage.form.gamesLoadError')}
                </FormDescription>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
        {canConfigure && (
          <FormField
            control={form.control}
            name="configurations"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <fieldset className="grid gap-2">
                    <legend
                      data-error={fieldState.error !== undefined}
                      className="data-[error=true]:text-destructive mb-2 text-sm leading-none font-medium"
                    >
                      {t('cabinetsPage.form.configurations')}
                    </legend>
                    {games.options
                      .filter((option) => offered.includes(option.id))
                      .map((option) => (
                        <div
                          key={option.id}
                          className="grid grid-cols-[8rem_1fr] items-center gap-2"
                        >
                          <span className="text-sm">{option.name}</span>
                          <ConfigurationSelect
                            gameId={option.id}
                            gameName={option.name}
                            value={field.value[option.id] ?? USE_DEFAULT}
                            valueLabel={assignedLabel(record, option.id)}
                            inheritedConfigurationId={inheritedConfiguration(
                              option.id,
                            )}
                            disabled={pending}
                            onChange={(value) =>
                              field.onChange({
                                ...field.value,
                                [option.id]: value,
                              })
                            }
                          />
                        </div>
                      ))}
                    {offered.length === 0 && (
                      <p className="text-muted-foreground text-sm">
                        {t('cabinetsPage.form.noGamesOffered')}
                      </p>
                    )}
                  </fieldset>
                </FormControl>
                <FormDescription>
                  {t('cabinetsPage.form.configurationsHint')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <Button
          type="submit"
          disabled={formLoading || pending}
        >
          {t('cabinetsPage.form.save')}
        </Button>
      </form>
    </Form>
  )
}
